/**
 * @file snapshot.ts
 * @description Снимок «вида сайта» по частям (phase19.16 Р1; SB50 — одна
 * публикация на оформление, бренд, SEO, меню и ленту блога; SB52 — версии и
 * возврат; db-design-r1 §1, §3, §7 риск 2).
 *
 * Снимок — то, что лежит в колонках `SiteRevision.theme/brand/seo/menus/blog`
 * и то, во что сервер сериализует черновик (`SiteDraft*`) перед сравнением с
 * опубликованным. Здесь:
 *   - zod-схемы частей BRAND, SEO, BLOG (THEME — schema.ts, MENUS — menu.ts);
 *   - умолчания платформы для магазина без публикаций (SB4, SB76);
 *   - чтение сохранённого снимка с деградацией по частям (старые ревизии,
 *     восстановление из копии): битая часть заменяется умолчанием, а не
 *     роняет витрину;
 *   - канонический JSON (сравнение частей и отпечаток черновика SB51);
 *   - список медиа снимка (`SiteRevision.mediaIds`, «где используется»).
 *
 * Хэш отпечатка считает сервер (node:crypto): пакет используется и в
 * браузере, поэтому здесь только канонический JSON.
 *
 * Р2а (db-design-r2a, ответы 1, 2, 4): части CATALOG (`SiteRevision.catalog`
 * = `{ options }`) и PRODUCT_TEMPLATE (`SiteRevision.productTemplate` =
 * `{ sections: [{ key, isEnabled, payloadVersion: 2, payload, appearance }] }`).
 * NULL в колонке (ревизии до Р2а, магазин не трогал шаблон) — умолчание из
 * кода: `DEFAULT_SITE_CATALOG_OPTIONS` и `PRODUCT_TEMPLATE_DEFAULT()`.
 *
 * Р3г (db-design-r3g §4): часть COUNTERS (`SiteRevision.counters`) —
 * счётчики и правила согласия посетителя (counters.ts); NULL в колонке
 * (версии до Р3г) — умолчания «счётчиков нет, уведомление «Понятно»».
 *
 * «SEO-техника» v0.19.65 (SB146; db-design-seo-tech §1.4): документ навигации
 * `navigation` (`SiteRevision.navigation`, navigation.ts) — второй ключ части
 * MENUS рядом с `menus`; связь «часть → ключи снимка» — список
 * (`SITE_VIEW_PART_KEYS`). NULL в колонке (версии до v0.19.65) — умолчания
 * (крошки включены).
 *
 * Р3а (db-design-r3a §3.1): блоки шаблона читает общий
 * `readSnapshotSectionList` (site-section/snapshot-sections.ts) — тот же,
 * что у версий страниц и мест; поведение и тексты замечаний прежние.
 */

import { z } from "zod";

import { collectSectionMediaIds } from "../site-section/media-ids.js";
import { PRODUCT_TEMPLATE_DEFAULT } from "../site-section/product-template.js";
import { readSnapshotSectionList, type SnapshotSectionIssue } from "../site-section/snapshot-sections.js";
import type { SectionLegacyContext } from "../site-section/upgrade-v4.js";
import { SECTION_PAYLOAD_VERSION, sectionPayloadSchema, type SectionPayload } from "../site-section/schema.js";
import { deepFreeze } from "./deep-freeze.js";
import { DEFAULT_SITE_BLOG_OPTIONS, readBlogOptions, siteBlogOptionsSchema } from "./blog-options.js";
import { DEFAULT_SITE_CATALOG_OPTIONS, readCatalogOptions, siteCatalogOptionsSchema } from "./catalog-options.js";
import { siteContactItemSchema, type SiteContactItem } from "./contacts.js";
import { DEFAULT_SITE_COUNTERS, readCounters, type SiteCounters } from "./counters.js";
import {
  DEFAULT_SITE_NAVIGATION,
  readNavigationOptions,
  readNavigationOptionsWithIssues,
  type SiteNavigationOptions,
} from "./navigation.js";
import { SITE_THEME_SCHEMA } from "./keys.js";
import { SITE_LIMITS } from "./limits.js";
import {
  emptySiteMenus,
  siteMenusSnapshotSchema,
  type SiteMenuNode,
  type SiteMenus,
} from "./menu.js";
import type { SiteViewPart } from "./parts.js";
import { SITE_VIEW_PARTS } from "./parts.js";
import { PLATFORM_FONT_REGISTRY, type SiteFontRegistry } from "./fonts.js";
import type { SiteDensityInput } from "./legacy-v1.js";
import { DEFAULT_SITE_THEME, readSiteTheme, type SiteTheme } from "./schema.js";

/** Версия формата снимка (`SiteRevision.schema`) = версия документа оформления. */
export const SITE_VIEW_SNAPSHOT_SCHEMA = SITE_THEME_SCHEMA;

/** Длины полей (uiux-design-r1 §4–§7, контракт site-view-api-contract §1). */
export const SITE_VIEW_FIELD_LIMITS = {
  siteName: 40,
  tagline: 60,
  footerSignature: 60,
  copyrightText: 120,
  workingHours: 120,
  seoDefaultTitle: 120,
  seoTitleSuffix: 40,
  seoDescription: 300,
  siteVerification: 200,
  appShortName: 12,
  blogEyebrow: 60,
  blogTitle: 60,
  blogSubtitle: 160,
  blogSeoTitle: 120,
  blogSeoDescription: 300,
  rssTitle: 120,
  rssDescription: 300,
  /** Название дизайна (db-design-designs §1). */
  designName: 60,
} as const;

const L = SITE_VIEW_FIELD_LIMITS;
const mediaId = z.string().min(1).max(64).nullable();
const text = (max: number) => z.string().max(max).nullable();

// ── BRAND ──

export const siteBrandSnapshotSchema = z.strictObject({
  siteName: z.string().min(1).max(L.siteName),
  tagline: text(L.tagline),
  logoLightMediaId: mediaId,
  logoDarkMediaId: mediaId,
  /** SB71: значок вкладки — часть «Бренд» (отступление от db-design-r1, контракт §0). */
  faviconMediaId: mediaId,
  contacts: z.array(siteContactItemSchema).max(SITE_LIMITS.contacts),
  footerSignature: text(L.footerSignature),
  footerDecorMediaId: mediaId,
  /** Шаблон с `{year}`; null → «© {year} {siteName}». */
  copyrightText: text(L.copyrightText),
  /** Часы работы — текст подвала и LocalBusiness.openingHours (uiux-design-r1 §4). */
  workingHours: text(L.workingHours),
  /** SB86: страница «Условия доставки и оплаты» (SitePage.id). */
  deliveryTermsPageId: mediaId,
});

export type SiteBrandSnapshot = z.infer<typeof siteBrandSnapshotSchema>;

// ── SEO ──

/**
 * Код подтверждения Яндекс Вебмастера / Google Search Console — только
 * значение `content` мета-тега (фаза §7.7): без `<`, `>` и кавычек, чтобы в
 * разметку витрины нельзя было внедрить ничего, кроме значения атрибута.
 */
export const SITE_VERIFICATION_PATTERN = /^[^<>"'`\s][^<>"'`\s]*$/;

const verificationCode = z
  .string()
  .max(L.siteVerification)
  .regex(SITE_VERIFICATION_PATTERN, "Код подтверждения — только значение content, без кавычек и угловых скобок")
  .nullable();

export const siteSeoSnapshotSchema = z.strictObject({
  defaultTitle: text(L.seoDefaultTitle),
  /** SB88: целиком, с разделителем: « — Название». */
  titleSuffix: text(L.seoTitleSuffix),
  description: text(L.seoDescription),
  ogImageMediaId: mediaId,
  /** short_name для manifest; null → название сайта. */
  appShortName: text(L.appShortName),
  /** false → robots `Disallow: /` и noindex на всём сайте. */
  allowIndexing: z.boolean(),
  /** Коды подтверждения сайта в Яндекс Вебмастере и Google Search Console (§7.7). */
  verification: z.strictObject({ yandex: verificationCode, google: verificationCode }),
});

export type SiteSeoSnapshot = z.infer<typeof siteSeoSnapshotSchema>;

// ── BLOG ──

export const siteBlogSnapshotSchema = z.strictObject({
  eyebrow: text(L.blogEyebrow),
  title: text(L.blogTitle),
  subtitle: text(L.blogSubtitle),
  coverMediaId: mediaId,
  /** Заголовок вкладки ленты; null → из title/названия сайта и окончания SEO. */
  seoTitle: text(L.blogSeoTitle),
  seoDescription: text(L.blogSeoDescription),
  rssTitle: text(L.rssTitle),
  rssDescription: text(L.rssDescription),
  /** Р2: вид ленты и статьи (blog-options.ts); старые ревизии без ключа — умолчание. */
  options: siteBlogOptionsSchema,
});

export type SiteBlogSnapshot = z.infer<typeof siteBlogSnapshotSchema>;

// ── CATALOG (Р2а) ──

export const siteCatalogSnapshotSchema = z.strictObject({
  /** Вид каталога и карточки товара (catalog-options.ts). */
  options: siteCatalogOptionsSchema,
});

export type SiteCatalogSnapshot = z.infer<typeof siteCatalogSnapshotSchema>;

// ── PRODUCT_TEMPLATE (Р2а) ──

/**
 * Блок шаблона страницы товара в снимке. `key` — id строки `SitePageBlock`
 * (по нему «что изменено» и возврат версии сопоставляют блоки); `isEnabled`
 * хранится, чтобы возврат версии восстановил и выключенные блоки;
 * `appearance` — эффекты появления блока (проверяет сервер при записи).
 */
export const siteProductTemplateSectionSchema = z.strictObject({
  key: z.string().min(1).max(64),
  isEnabled: z.boolean(),
  payloadVersion: z.literal(SECTION_PAYLOAD_VERSION),
  payload: sectionPayloadSchema,
  appearance: z.record(z.string(), z.unknown()).nullable(),
});

export type SiteProductTemplateSection = z.infer<typeof siteProductTemplateSectionSchema>;

export const siteProductTemplateSnapshotSchema = z.strictObject({
  sections: z.array(siteProductTemplateSectionSchema).max(SITE_LIMITS.productTemplateSections),
});

export type SiteProductTemplateSnapshot = z.infer<typeof siteProductTemplateSnapshotSchema>;

/** Снимок «вида сайта» целиком — порядок ключей = порядок частей. */
export interface SiteViewSnapshot {
  theme: SiteTheme;
  brand: SiteBrandSnapshot;
  seo: SiteSeoSnapshot;
  menus: SiteMenus;
  blog: SiteBlogSnapshot;
  catalog: SiteCatalogSnapshot;
  productTemplate: SiteProductTemplateSnapshot;
  /** Р3г: счётчики и правила согласия посетителя (counters.ts). */
  counters: SiteCounters;
  /**
   * «SEO-техника» (SB146): настройки навигации — часть MENUS (navigation.ts).
   * Необязательно в типе только на переходный период волны 2 (снимки,
   * собранные в apps/api литералом, ещё без ключа): отсутствие = умолчания
   * (`readNavigationOptions`). `readSiteViewSnapshot` и `defaultSiteViewSnapshot`
   * ставят ключ всегда.
   */
  navigation?: SiteNavigationOptions;
}

/**
 * Ключи снимка по частям (`THEME` → `["theme"]`). Часть MENUS владеет двумя
 * ключами: строки меню (`menus`) и документ навигации (`navigation`,
 * SB146) — «Отменить изменения» и «Вернуть версию» части переносят оба.
 */
export const SITE_VIEW_PART_KEYS: Readonly<Record<SiteViewPart, readonly (keyof SiteViewSnapshot)[]>> = deepFreeze({
  THEME: ["theme"],
  BRAND: ["brand"],
  SEO: ["seo"],
  MENUS: ["menus", "navigation"],
  BLOG: ["blog"],
  CATALOG: ["catalog"],
  PRODUCT_TEMPLATE: ["productTemplate"],
  COUNTERS: ["counters"],
});

// ── Умолчания платформы (SB4, SB76) ──

export function defaultSiteBrand(siteName: string): SiteBrandSnapshot {
  return {
    siteName: siteName.trim().slice(0, L.siteName) || "Магазин",
    tagline: null,
    logoLightMediaId: null,
    logoDarkMediaId: null,
    faviconMediaId: null,
    contacts: [],
    footerSignature: null,
    footerDecorMediaId: null,
    copyrightText: null,
    workingHours: null,
    deliveryTermsPageId: null,
  };
}

export function defaultSiteSeo(): SiteSeoSnapshot {
  return {
    defaultTitle: null,
    titleSuffix: null,
    description: null,
    ogImageMediaId: null,
    appShortName: null,
    allowIndexing: true,
    verification: { yandex: null, google: null },
  };
}

export function defaultSiteBlog(): SiteBlogSnapshot {
  return {
    eyebrow: null,
    title: null,
    subtitle: null,
    coverMediaId: null,
    seoTitle: null,
    seoDescription: null,
    rssTitle: null,
    rssDescription: null,
    options: JSON.parse(JSON.stringify(DEFAULT_SITE_BLOG_OPTIONS)) as SiteBlogSnapshot["options"],
  };
}

/** Р2а: вид каталога по умолчанию (как витрина до Р2а). */
export function defaultSiteCatalog(): SiteCatalogSnapshot {
  return { options: JSON.parse(JSON.stringify(DEFAULT_SITE_CATALOG_OPTIONS)) as SiteCatalogSnapshot["options"] };
}

/** Блок шаблона из payload (ключ — для стандартного шаблона и «Сбросить к стандартному»). */
export function productTemplateSection(key: string, payload: SectionPayload, isEnabled = true): SiteProductTemplateSection {
  return { key, isEnabled, payloadVersion: SECTION_PAYLOAD_VERSION, payload, appearance: null };
}

/**
 * Р2а: стандартный шаблон страницы товара; ключи блоков — `default-1`…
 * Плотность магазина — для тех же пикселей, что до v3 (SB120 этап 1; нет — «Обычно»).
 */
export function defaultSiteProductTemplate(density?: SiteDensityInput): SiteProductTemplateSnapshot {
  return {
    sections: PRODUCT_TEMPLATE_DEFAULT(density).map((payload, i) => productTemplateSection(`default-${i + 1}`, payload)),
  };
}

/** SB76: меню нового магазина — один пункт «Каталог» в шапке. */
export function defaultSiteMenus(): SiteMenus {
  const menus = emptySiteMenus();
  menus.HEADER.push({
    label: "Каталог",
    linkType: "SYSTEM",
    sitePageId: null,
    categoryId: null,
    contentCategoryId: null,
    contentTagId: null,
    legalDocumentId: null,
    systemRoute: "CATALOG",
    url: null,
    autoLimit: null,
    openInNewTab: false,
    isHidden: false,
    children: [],
  });
  return menus;
}

/** Вид сайта магазина без черновика и публикаций (SB4). */
export function defaultSiteViewSnapshot(siteName: string): SiteViewSnapshot {
  return {
    theme: JSON.parse(JSON.stringify(DEFAULT_SITE_THEME)) as SiteTheme,
    brand: defaultSiteBrand(siteName),
    seo: defaultSiteSeo(),
    menus: defaultSiteMenus(),
    blog: defaultSiteBlog(),
    catalog: defaultSiteCatalog(),
    productTemplate: defaultSiteProductTemplate(),
    counters: { ...DEFAULT_SITE_COUNTERS },
    navigation: { ...DEFAULT_SITE_NAVIGATION },
  };
}

// ── Чтение сохранённого снимка ──

/** Сырые колонки ревизии (`SiteRevision`). */
export interface SiteViewSnapshotColumns {
  schema: number;
  theme: unknown;
  brand: unknown;
  seo: unknown;
  menus: unknown;
  blog: unknown;
  /** Р2а: нет ключа или NULL — умолчание. */
  catalog?: unknown;
  /** Р2а: нет ключа или NULL — стандартный шаблон. */
  productTemplate?: unknown;
  /** Р3г: нет ключа или NULL — умолчания (счётчиков нет). */
  counters?: unknown;
  /** «SEO-техника» (SB146): нет ключа или NULL — умолчания (крошки включены). */
  navigation?: unknown;
}

/** Замечание чтения: что заменено умолчанием (для журнала сервера). */
export interface SiteViewReadIssue {
  part: SiteViewPart;
  path: string;
  message: string;
}

export interface SiteViewReadResult {
  snapshot: SiteViewSnapshot;
  replaced: SiteViewReadIssue[];
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** Отсутствующие ключи — из умолчаний (новые поля в старых ревизиях). */
function withDefaults<T extends object>(defaults: T, raw: unknown): Record<string, unknown> {
  return { ...(defaults as Record<string, unknown>), ...(isPlainObject(raw) ? raw : {}) };
}

function readPart<T extends object>(
  part: SiteViewPart,
  schema: z.ZodType<T>,
  defaults: T,
  raw: unknown,
  replaced: SiteViewReadIssue[],
): T {
  const parsed = schema.safeParse(withDefaults(defaults, raw));
  if (parsed.success) return parsed.data;
  replaced.push({ part, path: "", message: `Часть ${part} не прошла проверку — взято умолчание` });
  return defaults;
}

/**
 * Бренд читается мягче остальных: неверный контакт (реестр видов мог
 * ужесточиться после публикации) отбрасывается поштучно, а не обнуляет весь
 * бренд вместе с логотипами.
 */
function readBrand(raw: unknown, siteName: string, replaced: SiteViewReadIssue[]): SiteBrandSnapshot {
  const defaults = defaultSiteBrand(siteName);
  const merged = withDefaults(defaults, raw);
  const contacts: SiteContactItem[] = [];
  const rawContacts = Array.isArray(merged["contacts"]) ? merged["contacts"] : [];
  rawContacts.forEach((item, index) => {
    const parsed = siteContactItemSchema.safeParse(item);
    if (parsed.success) contacts.push(parsed.data);
    else replaced.push({ part: "BRAND", path: `contacts.${index}`, message: "Контакт отброшен" });
  });
  merged["contacts"] = contacts.slice(0, SITE_LIMITS.contacts);
  return readPart("BRAND", siteBrandSnapshotSchema, defaults, merged, replaced);
}

/**
 * Лента блога: параметры вида (Р2) читаются по полям — битый параметр не
 * обнуляет заголовки и обложку ленты.
 */
function readBlog(raw: unknown, replaced: SiteViewReadIssue[]): SiteBlogSnapshot {
  const rawBlog = isPlainObject(raw) ? raw : {};
  const options = readBlogOptions(rawBlog["options"]);
  for (const issue of options.replaced) {
    replaced.push({ part: "BLOG", path: issue.path ? `options.${issue.path}` : "options", message: issue.reason });
  }
  return readPart("BLOG", siteBlogSnapshotSchema, defaultSiteBlog(), { ...rawBlog, options: options.options }, replaced);
}

/** Каталог: параметры читаются по полям (битое поле не обнуляет остальные). */
function readCatalog(raw: unknown, replaced: SiteViewReadIssue[]): SiteCatalogSnapshot {
  const rawCatalog = isPlainObject(raw) ? raw : {};
  const options = readCatalogOptions(rawCatalog["options"]);
  for (const issue of options.replaced) {
    replaced.push({ part: "CATALOG", path: issue.path ? `options.${issue.path}` : "options", message: issue.reason });
  }
  return { options: options.options };
}

/**
 * Шаблон страницы товара: NULL — стандартный шаблон (без замечаний); блоки
 * читаются поштучно через `readSectionPayload` в контексте productTemplate —
 * битый блок чинится, а не обнуляет шаблон. Обязательные элементы здесь не
 * дорисовываются: это страховка витрины (ответ 2а), а сервер не даёт
 * опубликовать шаблон без них.
 *
 * SB120 этап 1 (db-design-sb120-stage1 §3): блоки v2 поднимаются плотностью
 * **той же версии** (`opts.density` из её `theme`) — версии неизменяемы, при
 * откате каждая поднимается своей плотностью.
 *
 * SB120 этап 3а (db-design-sb120-stage3 §4): блоки v2/v3 поднимаются до v4
 * контекстом той же версии — `opts.legacy` = плотность и шкала заголовков
 * из её `theme`; стандартный шаблон зависит только от плотности.
 */
export function readProductTemplateSnapshot(
  raw: unknown,
  replaced: SiteViewReadIssue[] = [],
  // `density?: never` — прежний ключ снят в v4; never ловит и распространение `...{ density }`.
  opts: { legacy?: SectionLegacyContext; density?: never } = {},
): SiteProductTemplateSnapshot {
  const density = opts.legacy?.density;
  if (raw === null || raw === undefined) return defaultSiteProductTemplate(density);
  const rawSections = isPlainObject(raw) ? raw["sections"] : undefined;
  if (!Array.isArray(rawSections)) {
    replaced.push({ part: "PRODUCT_TEMPLATE", path: "sections", message: "Шаблон страницы товара не прочитан — взят стандартный" });
    return defaultSiteProductTemplate(density);
  }
  // Р3а: общий читатель списка секций (тот же у версий страниц и мест).
  const issues: SnapshotSectionIssue[] = [];
  const sections = readSnapshotSectionList(
    rawSections,
    {
      context: "productTemplate",
      limit: SITE_LIMITS.productTemplateSections,
      ...(opts.legacy !== undefined ? { legacy: opts.legacy } : {}),
    },
    issues,
  );
  for (const issue of issues) replaced.push({ part: "PRODUCT_TEMPLATE", ...issue });
  return { sections };
}

/** Р3г: счётчики читаются по полям — битое поле не выключает остальное. */
function readCountersPart(raw: unknown, replaced: SiteViewReadIssue[]): SiteCounters {
  const result = readCounters(raw);
  for (const issue of result.replaced) {
    replaced.push({ part: "COUNTERS", path: issue.path, message: issue.reason });
  }
  return result.counters;
}

/** «SEO-техника»: навигация читается по полям; замечания — к части MENUS. */
function readNavigationPart(raw: unknown, replaced: SiteViewReadIssue[]): SiteNavigationOptions {
  const result = readNavigationOptionsWithIssues(raw);
  for (const issue of result.replaced) {
    replaced.push({ part: "MENUS", path: issue.path ? `navigation.${issue.path}` : "navigation", message: issue.reason });
  }
  return result.options;
}

function readMenus(raw: unknown, replaced: SiteViewReadIssue[]): SiteMenus {
  const parsed = siteMenusSnapshotSchema.safeParse(raw);
  if (parsed.success) return parsed.data as SiteMenus;
  replaced.push({ part: "MENUS", path: "", message: "Меню не прошло проверку — взято умолчание" });
  return defaultSiteMenus();
}

/**
 * Чтение ревизии (витрина, откат, «Отменить изменения»). Ошибка версии
 * (данные новее кода) пробрасывается — см. `upgradeTheme`.
 * `registry` — шрифты магазина (SB99): без него свои шрифты `shop:` читаются
 * как неизвестные и заменяются системным.
 */
export function readSiteViewSnapshot(
  columns: SiteViewSnapshotColumns,
  siteName: string,
  registry: SiteFontRegistry = PLATFORM_FONT_REGISTRY,
): SiteViewReadResult {
  const replaced: SiteViewReadIssue[] = [];
  const theme = readSiteTheme(columns.theme, columns.schema, undefined, registry);
  for (const issue of theme.replaced) {
    replaced.push({ part: "THEME", path: issue.path, message: issue.message });
  }
  return {
    snapshot: {
      theme: theme.theme,
      brand: readBrand(columns.brand, siteName, replaced),
      seo: readPart("SEO", siteSeoSnapshotSchema, defaultSiteSeo(), columns.seo, replaced),
      menus: readMenus(columns.menus, replaced),
      blog: readBlog(columns.blog, replaced),
      catalog: readCatalog(columns.catalog, replaced),
      productTemplate: readProductTemplateSnapshot(columns.productTemplate, replaced, {
        legacy: { density: theme.theme.spacing.density, headingScale: theme.theme.type.headingScale },
      }),
      counters: readCountersPart(columns.counters, replaced),
      navigation: readNavigationPart(columns.navigation, replaced),
    },
    replaced,
  };
}

// ── Канонический JSON и сравнение ──

/**
 * JSON с ключами объектов по алфавиту на любой глубине (порядок элементов
 * массивов сохраняется — он значим: порядок пунктов меню). Одинаковые
 * значения дают одинаковую строку независимо от порядка ключей в БД.
 */
export function canonicalJson(value: unknown): string {
  const normalize = (v: unknown): unknown => {
    if (Array.isArray(v)) return v.map(normalize);
    if (isPlainObject(v)) {
      const out: Record<string, unknown> = {};
      for (const key of Object.keys(v).sort()) {
        if (v[key] !== undefined) out[key] = normalize(v[key]);
      }
      return out;
    }
    return v;
  };
  return JSON.stringify(normalize(value));
}

/**
 * Значение ключа снимка для сравнения: навигация без ключа (снимок собран в
 * обход `readSiteViewSnapshot`) сравнивается как умолчания — иначе MENUS
 * считалась бы изменённой там, где владелец ничего не трогал.
 */
function partKeyValue(snapshot: SiteViewSnapshot, key: keyof SiteViewSnapshot): unknown {
  return key === "navigation" ? readNavigationOptions(snapshot.navigation) : snapshot[key];
}

/**
 * Изменённые части: канонический JSON хотя бы одного ключа части
 * различается (db-design-r1 §3 п.3; MENUS — `menus` и `navigation`).
 */
export function changedSiteViewParts(
  before: SiteViewSnapshot,
  after: SiteViewSnapshot,
): SiteViewPart[] {
  return SITE_VIEW_PARTS.filter((part) =>
    SITE_VIEW_PART_KEYS[part].some(
      (key) => canonicalJson(partKeyValue(before, key)) !== canonicalJson(partKeyValue(after, key)),
    ),
  );
}

// ── Медиа снимка ──

function menuNodes(menus: SiteMenus): SiteMenuNode[] {
  const all: SiteMenuNode[] = [];
  const walk = (nodes: readonly SiteMenuNode[]): void => {
    for (const node of nodes) {
      all.push(node);
      walk(node.children);
    }
  };
  walk(menus.HEADER);
  walk(menus.FOOTER);
  return all;
}

/** Медиа по частям — «где используется» и проверка своего магазина. */
export function siteViewPartMediaIds(snapshot: SiteViewSnapshot, part: SiteViewPart): string[] {
  const { brand, seo, blog, theme } = snapshot;
  let ids: Array<string | null>;
  switch (part) {
    case "THEME":
      ids = [...theme.backdrop.images.map((img) => img.mediaId), theme.backdrop.image?.mediaId ?? null];
      break;
    case "BRAND":
      ids = [brand.logoLightMediaId, brand.logoDarkMediaId, brand.faviconMediaId, brand.footerDecorMediaId];
      break;
    case "SEO":
      ids = [seo.ogImageMediaId];
      break;
    case "BLOG":
      ids = [blog.coverMediaId];
      break;
    case "PRODUCT_TEMPLATE": {
      // Медиа всех блоков шаблона (фоны, рисунки, фото общих элементов) без повторов.
      const all = new Set<string>();
      for (const s of snapshot.productTemplate.sections) collectSectionMediaIds(s.payload).forEach((id) => all.add(id));
      ids = [...all].sort();
      break;
    }
    case "MENUS":
    case "CATALOG":
    case "COUNTERS":
      ids = [];
      break;
  }
  return ids.filter((id): id is string => id !== null);
}

/** Все медиа снимка без повторов, по алфавиту (`SiteRevision.mediaIds`, `SiteDraft.mediaIds`). */
export function siteViewMediaIds(snapshot: SiteViewSnapshot): string[] {
  const ids = new Set<string>();
  for (const part of SITE_VIEW_PARTS) {
    for (const id of siteViewPartMediaIds(snapshot, part)) ids.add(id);
  }
  return [...ids].sort();
}

/** Пункты меню снимка плоским списком (оба места, сверху вниз). */
export function flattenSiteMenus(menus: SiteMenus): SiteMenuNode[] {
  return menuNodes(menus);
}
